import { useEffect } from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { initBuildFlags, initializeFeatureFlags } from 'application/store';
import { createLogger } from 'application/utils/LogUtils';
import { useAppDispatch } from 'application/providers/StoreProvider';

import { AuthCurrentUserDocument } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { UserDataType } from 'domain/auth/types';

const logger = createLogger('useFeatureFlagsSync', '#ffe66d');

export const useFeatureFlagsSync = (client: ApolloClient<NormalizedCacheObject>, userData?: UserDataType) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!userData?.mainCompany?.uiFeatures) {
      logger.info('Init build feature flags from env');
      dispatch(initBuildFlags());

      return;
    }

    logger.info('Syncing feature flags from user data:', userData.mainCompany.uiFeatures);

    dispatch(initializeFeatureFlags(userData.mainCompany.uiFeatures));
  }, [userData?.mainCompany?.uiFeatures, dispatch]);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    if (userData?.id) {
      const observable = client.watchQuery({
        query: AuthCurrentUserDocument,
        fetchPolicy: FetchPolicyKeys.CACHE_ONLY,
      });

      const subscription = observable.subscribe({
        next: ({ data }) => {
          const updatedUser = data?.currentUserPureQuery;

          if (updatedUser?.mainCompany?.uiFeatures) {
            logger.info('Apollo cache updated, syncing feature flags');
            dispatch(initializeFeatureFlags(updatedUser.mainCompany.uiFeatures));
          }
        },
        error: (error) => logger.error('Feature flags sync error:', error),
      });
      unsubscribe = () => subscription.unsubscribe();
    }

    return () => {
      unsubscribe?.();
    };
  }, [userData?.id, client, dispatch]);
};
